import React, {useEffect, useState, useContext} from 'react';
import {useParams} from 'react-router-dom';
import {Card, Typography, Divider, Tag, Space, Collapse, Alert} from 'antd';
import {Helmet} from 'react-helmet';
import {capitalize} from '@material-ui/core';
import Navbar from '../../components/Navbars/Navbar2';
import Footer from '../../components/Footer/Footer';
import {UserContext} from '../../utils/UserContext';
import './JobPage.scss';
import Loading from '../../components/Loaders/Loading';
import {getJobById, getRecommendedJobBasedOnSingleJob, trackJobImpression} from '../../api/jobsApi';
import ApplyJobButton from '../../components/ApplyJob/ApplyJobButton';
import {permColors} from '../../utils/colors';
import {Briefcase, Clock, MapPin, Calendar, Laptop, Heart, Timer, Building, DollarSign, Globe} from 'lucide-react';
import {daysAgo} from '../../utils/DateFunctions';
import {formatCurrency} from '../../utils/currency';
import {ROUTES} from '../../utils/ROUTES';
import SingleJobStats from '../../components/statistics/SingleJobStats';
import {descriptionStyle} from '../LandingPage/LandingPageCards';
import RecommendedJobs from '../../components/job-recommendation/RecommendedJobs';
import {HomeOutlined} from '@ant-design/icons';
import {Link} from 'react-router-dom';
import {Breadcrumb} from 'antd';

const {Text} = Typography;
const {Panel} = Collapse;

const formatCurrencyForMeta = (amountFrom, amountTo, rateType, currency) => {
	if (!amountFrom || !amountTo || !rateType || !currency) {
		return '';
	}

	const symbols = {
		USD: '$',
		PHP: '₱',
		CAD: 'CA$',
		AUD: 'A$',
		NZD: 'NZ$',
		GBP: '£',
	};

	const symbol = symbols[currency] || currency;
	return `${symbol}${amountFrom}-${symbol}${amountTo}/${rateType}`;
};

const stripHtml = (html) => {
	if (!html) return '';
	return html
		.replace(/<[^>]*>/g, '')
		.replace(/\s+/g, ' ')
		.trim();
};

const truncateText = (text, maxLength) => {
	if (!text) return '';
	if (text.length <= maxLength) return text;
	return text.substring(0, maxLength).trim() + '...';
};

const generateMetaDescription = (job) => {
	const parts = [];

	// Job title and company
	if (job?.title && job?.company_name) {
		parts.push(`${job.title} position at ${job.company_name}`);
	}

	// Salary information
	if (job?.payment_rate_from && job?.show_salary_on_job) {
		const salary = formatCurrencyForMeta(job.payment_rate_from, job.payment_rate_to, job.payment_rate_type, job.payment_rate_currency);
		if (salary) parts.push(`Salary: ${salary}`);
	}

	// Location
	if (job?.role_location_type) {
		const location = job.role_location_type === 'remote' ? 'Remote (Philippines)' : job?.role_location || job?.country;
		if (location) parts.push(`Location: ${location}`);
	} else if (job?.country) {
		parts.push(`Location: ${job.country}`);
	}

	// Job type and category
	if (job?.job_type) parts.push(job.job_type);
	if (job?.engagement_type) parts.push(job.engagement_type);

	// Description snippet
	if (job?.description) {
		const cleanDescription = stripHtml(job.description);
		const snippet = truncateText(cleanDescription, 100);
		if (snippet) parts.push(snippet);
	}

	const description = parts.join('. ') + ". Apply now on Permworks - Philippines' leading job platform.";

	// Ensure description is between 150-160 characters for optimal SEO
	return truncateText(description, 155);
};

const generateTitle = (job) => {
	if (!job?.title || !job?.company_name) {
		return 'Job Opening | Permworks';
	}

	// Create a concise but descriptive title
	const baseTitle = `${job.title} at ${job.company_name}`;

	// Add location if it's specific
	let locationPart = '';
	if (job?.role_location_type === 'remote') {
		locationPart = ' - Remote';
	} else if (job?.role_location && job.role_location !== job.country) {
		locationPart = ` - ${job.role_location}`;
	}

	const fullTitle = `${baseTitle}${locationPart} | Permworks`;

	// Ensure title is under 60 characters for optimal SEO
	return truncateText(fullTitle, 58);
};

const generateKeywords = (job) => {
	const keywords = [];

	if (job?.title) keywords.push(job.title);
	if (job?.company_name) keywords.push(job.company_name);
	if (job?.category) keywords.push(job.category);
	if (job?.job_type) keywords.push(job.job_type);
	if (job?.engagement_type) keywords.push(job.engagement_type);
	if (job?.country) keywords.push(`${job.country} jobs`);
	if (job?.role_location_type === 'remote') keywords.push('remote work', 'work from home');

	// Add generic job-related keywords
	keywords.push('jobs', 'careers', 'employment', 'Philippines jobs', 'hiring');

	return keywords.join(', ');
};

export default function JobPage() {
	const [job, setJob] = useState({});
	const {jobId} = useParams();
	const [currentUser] = useContext(UserContext);
	const [loading, setLoading] = useState(true);
	const [recommendedJobs, setRecommendedJobs] = useState([]);

	async function fetchRecommendedJobs() {
		try {
			const recommendedJobs = await getRecommendedJobBasedOnSingleJob(jobId);
			console.log(recommendedJobs);
			setRecommendedJobs(recommendedJobs);
		} catch (error) {
			console.error(error);
		}
	}

	async function fetchJob() {
		const jobData = await getJobById(jobId);
		trackJobImpression(jobId);
		setJob(jobData);
		setLoading(false);
	}

	useEffect(() => {
		fetchJob();
		fetchRecommendedJobs();
	}, [currentUser, jobId]);

	if (loading) {
		return <Loading />;
	}

	return (
		<div className="min-h-screen bg-backgroundGreyBlue">
			<Helmet>
				<title>{generateTitle(job)}</title>
				<meta name="description" content={generateMetaDescription(job)} />
				<link rel="canonical" href={`https://permworks.com${ROUTES.JOB_PAGE.replace(':jobId', job?.id)}`} />

				<meta property="og:title" content={generateTitle(job)} />
				<meta property="og:description" content={generateMetaDescription(job)} />
				<meta property="og:type" content="article" />
				<meta property="og:url" content={`https://permworks.com${ROUTES.JOB_PAGE.replace(':jobId', job?.id)}`} />
				<meta property="og:site_name" content="Permworks" />
				<meta property="og:locale" content="en_PH" />
				{job?.created_by_image && <meta property="og:image" content={job.created_by_image} />}
				<meta property="og:image:alt" content={`${job?.company_name || 'Company'} logo`} />

				<meta name="twitter:card" content="summary_large_image" />
				<meta name="twitter:title" content={generateTitle(job)} />
				<meta name="twitter:description" content={generateMetaDescription(job)} />
				{job?.created_by_image && <meta name="twitter:image" content={job.created_by_image} />}
				<meta name="twitter:site" content="@permworks" />

				<meta name="keywords" content={generateKeywords(job)} />

				<meta name="robots" content="index, follow, max-image-preview:large" />
				<meta name="author" content={job?.company_name || 'Permworks'} />
				{job?.created_at && <meta name="article:published_time" content={new Date(job.created_at).toISOString()} />}
				<meta name="article:section" content="Jobs" />
				{job?.category && <meta name="article:tag" content={job.category} />}

				<script type="application/ld+json">
					{JSON.stringify({
						'@context': 'https://schema.org/',
						'@type': 'JobPosting',
						title: job?.title,
						description: stripHtml(job?.description),
						identifier: {
							'@type': 'PropertyValue',
							name: job?.company_name,
							value: job?.id,
						},
						datePosted: job?.created_at,
						employmentType: job?.job_type?.toUpperCase(),
						hiringOrganization: {
							'@type': 'Organization',
							name: job?.company_name,
							logo: job?.created_by_image,
						},
						jobLocation: {
							'@type': 'Place',
							address: {
								'@type': 'PostalAddress',
								addressCountry: job?.country,
								addressLocality: job?.role_location,
							},
						},
						...(job?.payment_rate_from &&
							job?.show_salary_on_job && {
								baseSalary: {
									'@type': 'MonetaryAmount',
									currency: job?.payment_rate_currency,
									value: {
										'@type': 'QuantitativeValue',
										minValue: job?.payment_rate_from,
										maxValue: job?.payment_rate_to,
										unitText: job?.payment_rate_type?.toUpperCase(),
									},
								},
							}),
						workHours: job?.work_schedule_type === 'fixed' && job?.work_schedule_hours_start ? `${job.work_schedule_hours_start}-${job.work_schedule_hours_end}` : undefined,
						url: `https://permworks.com${ROUTES.JOB_PAGE.replace(':jobId', job?.id)}`,
					})}
				</script>
			</Helmet>
			<Navbar />

			<div className="bg-white border-b border-neutral-200/60">
				<div className="max-w-6xl mx-auto px-6 sm:px-6 lg:px-8 py-8 md:pt-14">
					<div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-6 max-w-5xl">
						<div className="flex-1">
							<div className="flex items-start gap-4 mb-6">
								<div className="flex-shrink-0">
									<div className="w-16 h-16 rounded-lg border border-neutral-200/60 flex items-center justify-center overflow-hidden p-1">
										{job?.created_by_image ? (
											<img src={job?.created_by_image} alt={`${job?.company_name || 'Company'} logo`} className="w-full h-full object-cover rounded-md" />
										) : (
											<Building className="w-8 h-8 text-neutral-400" />
										)}
									</div>
								</div>
								<div className="flex-1 min-w-0">
									<div className="flex items-center gap-3 mb-1">
										<h2 className="text-base sm:text-lg font-medium text-charcoal truncate">{job?.company_name && capitalize(job?.company_name)}</h2>
										{job?.created_at && (
											<span className="inline-flex items-center px-2.5 py-0.5 rounded-md text-xs font-medium bg-neutral-100 text-charcoal/70">
												{daysAgo(job?.created_at)} day{daysAgo(job?.created_at) === 1 ? '' : 's'} ago
											</span>
										)}
									</div>
									<h1 className="text-2xl sm:text-3xl font-semibold text-darkCharcoal mb-4 leading-tight">{job?.title && capitalize(job?.title)}</h1>

									<div className="flex flex-wrap gap-2">
										{job?.engagement_type && (
											<span className="inline-flex items-center px-3 py-1.5 rounded-md text-sm font-medium bg-permBlue/8 text-permBlue border border-permBlue/15">
												{job.engagement_type}
											</span>
										)}
										{job?.category && (
											<span className="inline-flex items-center px-3 py-1.5 rounded-md text-sm font-medium bg-emerald-50/80 text-emerald-700 border border-emerald-200/60">
												{job.category}
											</span>
										)}
										{job?.job_type && (
											<span className="inline-flex items-center px-3 py-1.5 rounded-md text-sm font-medium bg-neutral-100 text-charcoal border border-neutral-200">
												{job.job_type}
											</span>
										)}
									</div>
								</div>
							</div>
						</div>

						<div className="flex items-center justify-end">
							<ApplyJobButton job={job} buttonClassName="w-full sm:w-fit sm:min-w-[180px] min-h-[40px]" />
						</div>
					</div>
				</div>
			</div>

			<div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
				<Breadcrumb
					items={[
						{
							title: (
								<Link to={ROUTES.HOME} className="flex items-center gap-2 text-antBlue hover:text-permBlue">
									<HomeOutlined className="text-xs" />
									<span>Home</span>
								</Link>
							),
						},
						{
							title: (
								<Link to={ROUTES.BROWSE_JOBS} className="text-antBlue hover:text-permBlue">
									Browse Jobs
								</Link>
							),
						},

						{
							title: <span className="text-charcoal">{job?.title || 'Job Details'}</span>,
						},
					]}
					className="pb-2 pl-1"
				/>
				<div className="flex flex-col lg:flex-row gap-8">
					<div className="flex-1">
						<div className="bg-white rounded-lg border border-neutral-200/60 shadow-sm mb-6">
							<div className="p-6">
								<h2 className="text-lg font-medium text-charcoal mb-4">Job Overview</h2>
								<div className="grid grid-cols-1 md:grid-cols-2 gap-4">
									{job?.payment_rate_from && job?.show_salary_on_job && (
										<div className="flex items-start gap-3">
											<div className="flex-shrink-0 w-8 h-8 sm:w-10 sm:h-10 bg-white shadow-border-no-background rounded-xl flex items-center justify-center">
												<DollarSign className="w-4 h-4 text-charcoal" />
											</div>
											<div>
												<p className="text-xs sm:text-sm font-medium text-charcoal">Salary</p>
												<p className="text-xs sm:text-sm text-charcoal/70">
													{formatCurrency(job?.payment_rate_from, job?.payment_rate_to, job?.payment_rate_type, job?.payment_rate_currency)}
												</p>
											</div>
										</div>
									)}

									<div className="flex items-start gap-3">
										<div className="flex-shrink-0 w-8 h-8 sm:w-10 sm:h-10 bg-white shadow-border-no-background rounded-xl flex items-center justify-center">
											<MapPin className="w-4 h-4 text-charcoal" />
										</div>
										<div>
											<p className="text-xs sm:text-sm font-medium text-charcoal">Company Location</p>
											<p className="text-xs sm:text-sm text-charcoal/70">{job.country}</p>
										</div>
									</div>

									{job?.role_location_type && (
										<div className="flex items-start gap-3">
											<div className="flex-shrink-0 w-8 h-8 sm:w-10 sm:h-10 bg-white shadow-border-no-background rounded-xl flex items-center justify-center">
												<Globe className="w-4 h-4 text-charcoal" />
											</div>
											<div>
												<p className="text-xs sm:text-sm font-medium text-charcoal">Work Type</p>
												<p className="text-xs sm:text-sm text-charcoal/70 capitalize">
													{job.role_location_type} {job.role_location_type.toLowerCase() === 'remote' && '(Philippines)'}
												</p>
											</div>
										</div>
									)}

									{job?.role_location_type !== 'remote' && job?.role_location && (
										<div className="flex items-start gap-3">
											<div className="flex-shrink-0 w-8 h-8 sm:w-10 sm:h-10 bg-white shadow-border-no-background rounded-xl flex items-center justify-center">
												<MapPin className="w-4 h-4 text-charcoal" />
											</div>
											<div>
												<p className="text-xs sm:text-sm font-medium text-charcoal">Role Location</p>
												<p className="text-xs sm:text-sm text-charcoal/70 capitalize">{job.role_location}</p>
											</div>
										</div>
									)}

									{job?.work_schedule_timezone && (job?.work_schedule_type === 'fixed' || job?.work_schedule_type === 'shift-based') && (
										<div className="flex items-start gap-3">
											<div className="flex-shrink-0 w-8 h-8 sm:w-10 sm:h-10 bg-white shadow-border-no-background rounded-xl flex items-center justify-center">
												<Timer className="w-4 h-4 text-charcoal" />
											</div>
											<div>
												<p className="text-xs sm:text-sm font-medium text-charcoal">Timezone</p>
												<p className="text-xs sm:text-sm text-charcoal/70">{job.work_schedule_timezone}</p>
											</div>
										</div>
									)}

									{job?.work_schedule_type && (
										<div className="flex items-start gap-3">
											<div className="flex-shrink-0 w-8 h-8 sm:w-10 sm:h-10 bg-white shadow-border-no-background rounded-xl flex items-center justify-center">
												<Building className="w-4 h-4 text-charcoal" />
											</div>
											<div>
												<p className="text-xs sm:text-sm font-medium text-charcoal">Schedule Type</p>
												<p className="text-xs sm:text-sm text-charcoal/70 capitalize">{job.work_schedule_type}</p>
											</div>
										</div>
									)}
								</div>

								{job?.work_schedule_type === 'fixed' && job?.work_schedule_hours_start && (
									<div className="mt-6 pt-6 border-t border-neutral-200/60">
										<div className="flex items-start gap-3">
											<div className="flex-shrink-0 w-8 h-8 sm:w-10 sm:h-10 bg-white shadow-border-no-background rounded-xl flex items-center justify-center">
												<Clock className="w-4 h-4 text-charcoal" />
											</div>
											<div className="flex-1">
												<p className="text-xs sm:text-sm font-medium text-charcoal">Working Schedule</p>
												<div>
													<p className="text-xs sm:text-sm text-charcoal/70">
														<span className="font-medium">Hours:</span> {job.work_schedule_hours_start} - {job.work_schedule_hours_end}
													</p>
													{job?.work_schedule_days && (
														<p className="text-xs sm:text-sm text-charcoal/70">
															<span className="font-medium">Days:</span>{' '}
															{job.work_schedule_days
																.split(',')
																.map((day) => capitalize(day))
																.join(', ')}
														</p>
													)}
												</div>
											</div>
										</div>
									</div>
								)}

								{job?.shifts?.length > 0 && (
									<div className="mt-6 pt-6 border-t border-neutral-200/60">
										<div className="flex items-start gap-3">
											<div className="flex-shrink-0 w-8 h-8 sm:w-10 sm:h-10 bg-white shadow-border-no-background rounded-xl flex items-center justify-center">
												<Clock className="w-4 h-4 text-charcoal" />
											</div>
											<div className="flex-1">
												<p className="text-xs sm:text-sm font-medium text-charcoal">Available Shifts</p>
												<div>
													{job.shifts.map((shift, index) => (
														<div key={index} className="flex justify-between items-center py-2 px-3 bg-neutral-50/70 rounded-md">
															<span className="text-xs sm:text-sm font-medium text-charcoal">{shift.day_of_week}</span>
															<span className="text-xs sm:text-sm text-charcoal/70">
																{shift.start_time} - {shift.end_time}
															</span>
														</div>
													))}
												</div>
											</div>
										</div>
									</div>
								)}

								{(job?.equipment_allocation || job?.provide_HMO) && (
									<div className="mt-6 pt-6 border-t border-neutral-200/60">
										<div className="flex items-start gap-3">
											<div className="flex-shrink-0 w-8 h-8 sm:w-10 sm:h-10 bg-white shadow-border-no-background rounded-xl flex items-center justify-center">
												<Laptop className="w-4 h-4 text-charcoal" />
											</div>
											<div className="flex-1">
												<p className="text-xs sm:text-sm font-medium text-charcoal">Benefits & Equipment</p>
												<div>
													{job?.equipment_allocation && (
														<p className="text-xs sm:text-sm text-charcoal/70">
															<span className="font-medium">Equipment:</span> <span className="capitalize">{job.equipment_allocation}</span>
														</p>
													)}
													{job?.provide_HMO && (
														<p className="text-xs sm:text-sm text-charcoal/70">
															<span className="font-medium">Health Benefits:</span> HMO Provided
														</p>
													)}
												</div>
											</div>
										</div>
									</div>
								)}
							</div>
						</div>

						<div className="bg-white rounded-lg border border-neutral-200/60 shadow-sm mb-6">
							<div className="p-6">
								<h2 className="text-lg font-medium text-charcoal mb-4">Job Description</h2>
								{job?.description && (
									<div
										className={`prose prose-neutral max-w-none ${descriptionStyle}`}
										dangerouslySetInnerHTML={{
											__html: job?.description,
										}}
									/>
								)}
							</div>
						</div>

						<div className="bg-white rounded-lg border border-neutral-200/60 shadow-sm">
							<div className="p-6">
								<h2 className="text-lg font-medium text-charcoal mb-4">Application Requirements</h2>
								{job?.questions?.length > 0 ? (
									<div className="space-y-3">
										<p className="text-sm text-charcoal/70 mb-4">You'll need to answer the following question{job.questions.length > 1 ? 's' : ''} when applying:</p>
										<div className="space-y-3">
											{job.questions?.map((q, index) => (
												<div key={index} className="flex gap-3">
													<span className="flex-shrink-0 w-6 h-6 bg-permBlue/8 text-permBlue rounded-md flex items-center justify-center text-sm font-medium">
														{index + 1}
													</span>
													<p className="text-sm text-charcoal pt-0.5">{capitalize(q?.question)}</p>
												</div>
											))}
										</div>
									</div>
								) : (
									<div className="flex items-center gap-3 p-4 bg-emerald-50/60 border border-emerald-200/50 rounded-lg">
										<div className="flex-shrink-0 w-8 h-8 bg-emerald-100/70 rounded-lg flex items-center justify-center">
											<svg className="w-4 h-4 text-emerald-600" fill="currentColor" viewBox="0 0 20 20">
												<path
													fillRule="evenodd"
													d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
													clipRule="evenodd"
												/>
											</svg>
										</div>
										<div>
											<p className="text-sm font-medium text-emerald-800">No additional questions required</p>
											<p className="text-sm text-emerald-600">You can apply directly without answering any screening questions.</p>
										</div>
									</div>
								)}
							</div>
						</div>
					</div>

					<div className="lg:w-80">
						{currentUser?.id === job?.created_by ? (
							<div className="bg-white rounded-lg border border-neutral-200/60 shadow-sm">
								<div className="p-6">
									<SingleJobStats jobId={jobId} />
								</div>
							</div>
						) : (
							<RecommendedJobs jobs={recommendedJobs} />
						)}
					</div>
				</div>
			</div>

			<Footer backgroundColor={permColors.backgroundGreyBlue} />
		</div>
	);
}
